import {
  AgentProfile,
  AppPermission,
  AppPresence,
  UserRole
} from "@closerplatform/spinner-openapi";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { flatMap, uniq } from "lodash";
import { getRoot, Instance, types } from "mobx-state-tree";
import { IRootStore } from "..";
import { agentGroupsStoreGetter, roleStoreGetter } from "../utils";

export const Agent = types
  .model("Agent", {
    id: types.identifier,
    firstName: types.string,
    lastName: types.string,
    email: types.maybe(types.string),
    avatarToken: types.maybe(types.string),
    isBot: false,
    skills: types.array(types.string),
    isActive: true,
    role: types.optional(
      types.enumeration<UserRole>("Role", Object.values(UserRole)),
      UserRole.ADVISER
    ),
    presence: types.optional(
      types.enumeration<AppPresence>("PresenceStatus", Object.values(AppPresence)),
      AppPresence.Unavailable
    ),
    autoAssignedLimit: types.maybe(types.number),
    unavailabilityReason: types.maybe(types.string),
    isLoading: types.optional(types.boolean, false),
    orgRole: types.string,
    tags: types.array(types.string),
    tagGroups: types.array(types.string),
    groups: types.array(types.string)
  })
  .views(roleStoreGetter)
  .views(agentGroupsStoreGetter)
  .views((self) => {
    const getFullName = () => `${self.firstName} ${self.lastName}`.trim();
    const views = {
      /** Agent role check */
      get isAgent(): boolean {
        return true;
      },
      /** Guest role check */
      get isGuest(): boolean {
        return false;
      },
      /**
       * Get agent's full name
       * @return {string} full name
       */
      get fullName(): string {
        return getFullName();
      },
      /**
       * Get agent's displayed name
       * @return {string} displayed name
       */
      get displayedName(): string {
        const { currentUserStore } = getRoot(self) as IRootStore; // idk why, but it should be initialize here, not sooner by global getter
        return currentUserStore && currentUserStore.id === self.id
          ? `${self.firstName} (${intl.formatMessage("user.displayedName.you")})`
          : getFullName();
      },
      /**
       * Does this profile belong to a currently logged user?
       * @return {boolean} True/False if is/isn't current, undefined if unable to determine (eg. node's detached)
       */
      get isCurrent(): boolean | undefined {
        const { currentUserStore } = getRoot(self) as IRootStore;
        if (currentUserStore) {
          return currentUserStore.id === self.id;
        }
        return undefined;
      },

      get orgRoleReference() {
        return self.roleStore.getRoleById(self.orgRole);
      },

      hasTagGroup(tagGroupId: string): boolean {
        return self.tagGroups.includes(tagGroupId);
      },

      get allTagGroupIds(): string[] {
        const agentGroupTagGroups = flatMap(
          self.agentGroupsStore.getByIds(self.groups),
          (group) => group.tagGroupIds
        );
        return uniq([...self.tagGroups, ...agentGroupTagGroups]);
      }
    };
    return views;
  })
  .actions((self) => {
    const actions = {
      setPresence(presence: AppPresence, unavailabilityReason?: string) {
        self.presence = presence;
        self.unavailabilityReason = unavailabilityReason;
      },
      doesHavePermission(permission: AppPermission): boolean {
        return !!self.orgRoleReference?.findPermission(permission);
      },
      addGroup(groupId: string) {
        self.groups.push(groupId);
      },
      removeGroup(groupId: string) {
        self.groups.replace(self.groups.filter((id) => id !== groupId));
      },
      replaceTagGroups(tagGroups: string[]) {
        self.tagGroups.replace(tagGroups);
      }
    };

    return actions;
  });

export class AgentModel {
  public static fromApiDTO(profile: AgentProfile): IAgent {
    return Agent.create(profile);
  }
}

export interface IAgent extends Instance<typeof Agent> {}
